import React, { useCallback, useEffect } from 'react';
import { useToast } from '@chakra-ui/react';
import { matchTargetElement } from '../../utils';

const copyToastId = 'copy-toast';

/** Answer editors where copy/paste is still allowed in strict mode */
export const excludedSelectors = ['.cpp-editable'];

const withCopyPasteProtection = (WrappedComponent, options = {}) => {
  const WithProtection = (props) => {
    const toast = useToast();

    const strictMode =
      typeof options.enableStrictMode === 'function'
        ? options.enableStrictMode(props)
        : options.enableStrictMode !== undefined
          ? options.enableStrictMode
          : false;

    const showWarningToast = useCallback(() => {
      if (!toast.isActive(copyToastId)) {
        toast({
          id: copyToastId,
          title: 'Action not allowed',
          description: 'Copying and pasting is disabled in this assessment.',
          status: 'warning',
          duration: 3000,
          isClosable: true,
          position: 'bottom'
        });
      }
    }, [toast]);

    const blockIfProtected = useCallback(
      (event) => {
        const target = event?.target;
        if (matchTargetElement(target)) {
          return false;
        }
        event.preventDefault();
        showWarningToast();
        return true;
      },
      [showWarningToast]
    );

    const clearProtectedSelection = useCallback(() => {
      const selection = window.getSelection?.();
      if (!selection || selection.isCollapsed || !selection.toString()) {
        return;
      }

      const node = selection.anchorNode;
      const target =
        node?.nodeType === Node.ELEMENT_NODE ? node : node?.parentElement || document.activeElement;

      if (matchTargetElement(target)) {
        return;
      }

      selection.removeAllRanges();
      showWarningToast();
    }, [showWarningToast]);

    // Block native copy / cut / paste (desktop menus + mobile callouts)
    useEffect(() => {
      if (!strictMode) return;

      const handleClipboardEvent = (event) => {
        blockIfProtected(event);
      };

      document.addEventListener('copy', handleClipboardEvent, true);
      document.addEventListener('cut', handleClipboardEvent, true);
      document.addEventListener('paste', handleClipboardEvent, true);

      return () => {
        document.removeEventListener('copy', handleClipboardEvent, true);
        document.removeEventListener('cut', handleClipboardEvent, true);
        document.removeEventListener('paste', handleClipboardEvent, true);
      };
    }, [blockIfProtected, strictMode]);

    // Block long-press context menus (does not affect touch scrolling)
    useEffect(() => {
      if (!strictMode) return;

      const handleContextMenu = (event) => {
        blockIfProtected(event);
      };

      document.addEventListener('contextmenu', handleContextMenu, true);
      return () => {
        document.removeEventListener('contextmenu', handleContextMenu, true);
      };
    }, [blockIfProtected, strictMode]);

    // Block keyboard shortcuts (desktop / Bluetooth keyboards on mobile)
    useEffect(() => {
      if (!strictMode) return;

      const handleKeyDown = (event) => {
        if (matchTargetElement(event.target)) {
          return;
        }

        const key = event.key?.toLowerCase?.() || '';
        const isModifier = event.ctrlKey || event.metaKey;
        if (
          isModifier &&
          (key === 'c' || key === 'v' || key === 'x' || key === 'a' || key === 'insert')
        ) {
          event.preventDefault();
          showWarningToast();
        }

        if (event.shiftKey && key === 'insert') {
          event.preventDefault();
          showWarningToast();
        }
      };

      document.addEventListener('keydown', handleKeyDown, true);
      return () => {
        document.removeEventListener('keydown', handleKeyDown, true);
      };
    }, [showWarningToast, strictMode]);

    // Clear illicit selection after the gesture ends (avoid selectstart — it breaks mobile scroll)
    useEffect(() => {
      if (!strictMode) return;

      const handlePointerUp = () => {
        window.setTimeout(clearProtectedSelection, 0);
      };

      document.addEventListener('mouseup', handlePointerUp, true);
      document.addEventListener('touchend', handlePointerUp, true);

      return () => {
        document.removeEventListener('mouseup', handlePointerUp, true);
        document.removeEventListener('touchend', handlePointerUp, true);
      };
    }, [clearProtectedSelection, strictMode]);

    // Best-effort Clipboard API override
    useEffect(() => {
      if (!strictMode) return;
      if (!navigator.clipboard) return;

      const originalClipboardWrite = navigator.clipboard.writeText?.bind(navigator.clipboard);
      const originalClipboardRead = navigator.clipboard.readText?.bind(navigator.clipboard);

      if (originalClipboardWrite) {
        navigator.clipboard.writeText = function (...args) {
          if (matchTargetElement(document.activeElement)) {
            return originalClipboardWrite(...args);
          }
          showWarningToast();
          return Promise.reject(new Error('Clipboard writing is disabled'));
        };
      }

      if (originalClipboardRead) {
        navigator.clipboard.readText = function (...args) {
          if (matchTargetElement(document.activeElement)) {
            return originalClipboardRead(...args);
          }
          showWarningToast();
          return Promise.reject(new Error('Clipboard reading is disabled'));
        };
      }

      return () => {
        if (originalClipboardWrite) {
          navigator.clipboard.writeText = originalClipboardWrite;
        }
        if (originalClipboardRead) {
          navigator.clipboard.readText = originalClipboardRead;
        }
      };
    }, [showWarningToast, strictMode]);

    // Prevent drag-copy of text/images
    useEffect(() => {
      if (!strictMode) return;

      const handleDragStart = (event) => {
        blockIfProtected(event);
      };

      document.addEventListener('dragstart', handleDragStart, true);
      return () => {
        document.removeEventListener('dragstart', handleDragStart, true);
      };
    }, [blockIfProtected, strictMode]);

    // CSS: block selection without breaking iOS touch scrolling on overflow containers
    useEffect(() => {
      if (!strictMode) return;

      const styleElement = document.createElement('style');
      styleElement.setAttribute('data-copy-paste-protection', 'true');
      styleElement.textContent = `
        .copy-paste-protected {
          -webkit-touch-callout: none;
        }

        .copy-paste-protected *:not(input):not(textarea):not(.cpp-editable):not([contenteditable="true"]) {
          -webkit-user-select: none !important;
          -moz-user-select: none !important;
          -ms-user-select: none !important;
          user-select: none !important;
        }

        .copy-paste-protected input,
        .copy-paste-protected textarea,
        .copy-paste-protected [contenteditable="true"],
        .copy-paste-protected .cpp-editable,
        .copy-paste-protected .cpp-editable * {
          -webkit-user-select: text !important;
          -moz-user-select: text !important;
          -ms-user-select: text !important;
          user-select: text !important;
          -webkit-touch-callout: default !important;
        }

        /*
          iOS will not pan an element with user-select:none. Keep the Q&A
          scroller itself selectable-auto, and let pan gestures start on the
          question/solution content instead of creating nested scrollbars.
        */
        .copy-paste-protected .assignment-scroll {
          -webkit-user-select: auto !important;
          user-select: auto !important;
          -webkit-overflow-scrolling: touch !important;
          overscroll-behavior: contain;
        }

        .copy-paste-protected .assignment-scroll *:not(input):not(textarea):not(.cpp-editable):not([contenteditable="true"]) {
          touch-action: pan-x pan-y pinch-zoom;
        }

        .copy-paste-protected ::selection {
          background: transparent !important;
        }

        .copy-paste-protected input::selection,
        .copy-paste-protected textarea::selection,
        .copy-paste-protected .cpp-editable::selection,
        .copy-paste-protected .cpp-editable *::selection {
          background: Highlight !important;
          color: HighlightText !important;
        }

        .copy-paste-protected img {
          -webkit-user-drag: none !important;
          user-drag: none !important;
          pointer-events: none !important;
        }
      `;

      document.head.appendChild(styleElement);
      document.body.classList.add('copy-paste-protected');

      return () => {
        styleElement.remove();
        document.body.classList.remove('copy-paste-protected');
      };
    }, [strictMode]);

    const setSpellCheckFalse = (element) => {
      if (!React.isValidElement(element)) return element;
      if (element.type === 'input' || element.type === 'textarea') {
        return React.cloneElement(element, { spellCheck: false });
      }
      if (element.props && element.props.children) {
        return React.cloneElement(element, {
          children: React.Children.map(element.props.children, setSpellCheckFalse)
        });
      }
      return element;
    };

    if (!strictMode) {
      return setSpellCheckFalse(<WrappedComponent {...props} />);
    }

    return (
      <div className="copy-paste-protected flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden">
        {setSpellCheckFalse(<WrappedComponent {...props} />)}
      </div>
    );
  };

  WithProtection.displayName = `withCopyPasteProtection(${
    WrappedComponent.displayName || WrappedComponent.name || 'Component'
  })`;

  return WithProtection;
};

export default withCopyPasteProtection;
